import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AccountContact,
  Constants,
  ErrorReportingService,
  FullPageMaskService,
  ICountry,
  ILookup,
  SecurityService,
  strictEmailValidator,
} from 'common';
import { Subject, takeUntil } from 'rxjs';
import { ApplicationPermissions } from '../../../../models/application-permissions.model';
import { ContactService } from '../../../../services/contact.service';

@Component({
  selector: 'acc-new-contact',
  templateUrl: './new-contact.component.html',
  styleUrls: ['./new-contact.component.scss'],
})
export class NewContactComponent implements OnInit, OnDestroy {
  salutations: ILookup[];
  countries: ICountry[];
  counties: ILookup[];
  existingEmails: string[] = [];
  applicationPermissions: ApplicationPermissions;
  Permissions = Constants.permissionGroups;
  permissionSet = this.Permissions.hasNoOnlineAccess;

  private _destroy$ = new Subject<void>();

  newContactgroup = new UntypedFormGroup({
    salutation: new UntypedFormControl('', Validators.required),
    firstName: new UntypedFormControl('', Validators.required),
    lastName: new UntypedFormControl('', Validators.required),
    positionHeld: new UntypedFormControl('', Validators.required),
    isRegulatoryContact: new UntypedFormControl(false),
    isDirector: new UntypedFormControl(false),
    isSQUContact: new UntypedFormControl(false),

    addressLine1: new UntypedFormControl('', Validators.required),
    addressLine2: new UntypedFormControl(''),
    addressLine3: new UntypedFormControl(''),
    addressLine4: new UntypedFormControl(''),
    postCode: new UntypedFormControl(''),
    country: new UntypedFormControl('', Validators.required),
    county: new UntypedFormControl('', Validators.required),

    phone: new UntypedFormControl('', Validators.required),
    mobile: new UntypedFormControl(''),
    fax: new UntypedFormControl(''),
    email: new UntypedFormControl('', [
      Validators.required,
      strictEmailValidator,
      (control: AbstractControl): ValidationErrors | null =>
        this.existingEmails.includes(control.value) && control.value.length > 0 ? { emailExists: true } : null,
    ]),
  });

  showErrorCreateContact = false;
  errorMessages = [];

  public get providerTypeString(): string {
    return this.contactService.getProviderTypeString();
  }

  constructor(
    private route: ActivatedRoute,
    private security: SecurityService,
    private contactService: ContactService,
    private errorService: ErrorReportingService,
    private router: Router,
    private maskService: FullPageMaskService
  ) {}

  ngOnInit() {
    this.salutations = this.route.snapshot.data.salutations;
    this.countries = this.route.snapshot.data.countries;
    this.counties = this.route.snapshot.data.counties;
    this.existingEmails = this.route.snapshot.data.contacts.map((x) => x.email);
    this.applicationPermissions = this.route.snapshot.data.permissions;
    this.contactService.permissionChanges$.pipe(takeUntil(this._destroy$)).subscribe((newPermissions) => {
      this.permissionSet = newPermissions;
    });
    this.newContactgroup.controls.country.valueChanges.subscribe((country: string) => this.countryChanged(country));
    this.newContactgroup.controls.county.disable();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  save() {
    if (!this.newContactgroup.valid) {
      return;
    }
    const contact = new AccountContact(
      this.security.profile.selectedAccount.accountCode,
      0,

      this.getFormValue('salutation'),
      this.getFormValue('firstName'),
      this.getFormValue('lastName'),
      this.getFormValue('positionHeld'),
      this.getFormValue('isRegulatoryContact'),
      this.getFormValue('isSQUContact'),
      this.getFormValue('isDirector'),

      this.getFormValue('addressLine1'),
      this.getFormValue('addressLine2'),
      this.getFormValue('addressLine3'),
      this.getFormValue('addressLine4'),
      this.getFormValue('postCode'),
      this.getFormValue('county'),
      this.getFormValue('country'),

      this.getFormValue('phone'),
      this.getFormValue('mobile'),
      this.getFormValue('fax'),
      this.getFormValue('email'),

      this.permissionSet >= this.Permissions.hasOnlineAccess,
      false,
      this.permissionSet,
      null
    );

    this.showErrorCreateContact = false;
    this.maskService.showLoading();

    this.contactService
      .addContact(contact)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (wasRequestOk: boolean) => {
          this.showErrorCreateContact = !wasRequestOk;
          if (wasRequestOk) {
            this.showStatus();
          }
        },
        error: (response) => {
          this.errorMessages = this.errorService.getResponseDTOErrors(response);
          this.showErrorCreateContact = true;
          this.maskService.hideLoading();
        },
      });
  }

  countryChanged(value: string) {
    if (value.toLowerCase() === Constants.irelandCountryString.toLocaleLowerCase()) {
      this.newContactgroup.controls.county.enable();
    } else {
      this.newContactgroup.controls.county.patchValue('');
      this.newContactgroup.controls.county.disable();
    }
  }

  showStatus(): void {
    this.errorService.displayInfo('The new contact was added successfully');
    this.router.navigateByUrl('/accounts/details/contacts');
  }

  getFormValue(name: string) {
    return this.newContactgroup.get(name).value;
  }
}
